// React
import { useEffect, useState } from "react"

// Components
import { PrintTransaction } from "components"

// Form
import { useFormContext } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { PaymentDepositCustomerType } from "../types"

// Utils
import { useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { generatePath } from "pages/Task/utils/functions"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useCustomerDetail } from "../../CustomerOrder/utils/hooks" 
import { TableJournalEntry } from "./Tables"

export function Print(): JSX.Element {
  // Form
  const { formState: { defaultValues } } = useFormContext<PaymentDepositCustomerType>()
  
  // Hooks
  const company = useList()
  const country = useCountryById()
  const customer = useCustomerDetail(defaultValues?.customer_id ?? "")
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")

  // Vars
  const isLoading: boolean = company.isLoading || customer.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading
  const userToken = userTokenData()

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-3">
        <section className="grid grid-cols-2 items-stretch gap-3">
          <img
            src={generatePath(setting.dataCompany?.logo)}
            alt="Logo"
            width={200}
            height={50}
          />

          <section>
            <div className="mb-3 text-black text-xl font-bold text-right">Payment Deposit</div>

            <section className="flex justify-end">
              <table className="w-full max-w-[300px]">
                <tbody>
                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Reference :{" "}</td>
                    <td className="whitespace-nowrap text-right">{defaultValues?.trx_code}</td>
                  </tr>

                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Date :{" "}</td>
                    <td className="whitespace-nowrap text-right">{defaultValues?.transaction_date ? moment(defaultValues?.transaction_date).format("DD/MM/YYYY") : "-"}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </section>
        </section>

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Company Info</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{userToken?.company_name}</div>
            <div>{address},</div>
            <div>{regency.data?.regency_name},</div>
            <div>{province.data?.province_name},</div>
            <div>Telp: {setting.data?.phone ?? "-"}</div>
            <div>Email: {setting.data?.email ?? "-"}</div>
          </section>

          <section>
            <div className="font-bold">Bill For</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{defaultValues?.customer_name}</div>
            <div>Telp: {customer.data?.handphone}</div>
          </section>
        </section>

        <TableJournalEntry />

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Additional Notes</div>
            <div className="divider divider-neutral" />
            <td>{defaultValues?.memo}</td>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}